import { useCanEdit } from '@/auth/account';
import type { ReactNode } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * The move a drag represents: the ids in their new order, or null when the
 * drop is a no-op. Pure so it can be tested without a pointer – dnd-kit's own
 * sensors need real layout boxes, which jsdom does not provide.
 */
export function computeReorder<T extends { id: string }>(
  items: readonly T[],
  activeId: string,
  overId: string | null,
): string[] | null {
  if (overId === null || activeId === overId) return null;
  const from = items.findIndex((item) => item.id === activeId);
  const to = items.findIndex((item) => item.id === overId);
  if (from === -1 || to === -1) return null;
  return arrayMove([...items], from, to).map((item) => item.id);
}

interface SortableListProps<T extends { id: string }> {
  items: T[];
  onReorder: (orderedIds: string[]) => void;
  children: (item: T, index: number) => ReactNode;
  className?: string;
}

/**
 * Vertical drag & drop over an id list. The parent owns the data; this only
 * reports the new order, so an optimistic re-render and the API call stay in
 * one place. Keyboard sorting (space to lift, arrows to move) comes for free.
 */
export function SortableList<T extends { id: string }>({
  items,
  onReorder,
  children,
  className,
}: SortableListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    const ids = computeReorder(items, String(active.id), over ? String(over.id) : null);
    if (ids) onReorder(ids);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
        <ul className={cn('flex flex-col', className)}>
          {items.map((item, index) => children(item, index))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

interface SortableRowProps {
  id: string;
  children: ReactNode;
  className?: string;
  /** Accessible name for the drag handle, e.g. the row's title. */
  handleLabel: string;
}

export function SortableRow({ id, children, className, handleLabel }: SortableRowProps) {
  const canEdit = useCanEdit();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !canEdit,
  });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'relative flex items-stretch gap-1',
        isDragging && 'z-20 opacity-90',
        className,
      )}
    >
      {canEdit ? (
      <button
        type="button"
        aria-label={`${handleLabel} verschieben`}
        className={cn(
          'flex shrink-0 cursor-grab touch-none items-center rounded-md px-1 text-[var(--ink-3)]',
          'hover:bg-[var(--surface-2)] hover:text-[var(--ink-2)] active:cursor-grabbing',
        )}
        {...attributes}
        {...listeners}
      >
        <GripVertical size={16} aria-hidden="true" />
      </button>
      ) : null}
      <div className="min-w-0 flex-1">{children}</div>
    </li>
  );
}
